import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { OCPPClient } from '../services/ocppClient';
import { v4 as uuidv4 } from 'uuid';

interface WebSocketContextType {
  isConnected: boolean;
  stationId: string;
  setStationId: (id: string) => void;
  messages: any[];
  connect: () => void;
  disconnect: () => void;
  sendHeartbeat: () => void;
  sendBootNotification: () => void;
  ocppClient: OCPPClient | null;
  addOutgoingMessage: (action: string, payload: any, messageId: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [stationId, setStationId] = useState('CP004');
  const [messages, setMessages] = useState<any[]>([]);
  const [ocppClient, setOcppClient] = useState<OCPPClient | null>(null);

  const handleIncomingMessage = useCallback((message: any) => {
    console.log('Received raw OCPP message:', message);

    // --- 1. BROADCAST TO SIMULATOR ---
    // If it's a Request from Backend (messageTypeId: 2)
    if (message && typeof message === 'object' && message.messageTypeId === 2) {
      const { action, payload, messageId } = message;

      console.log(`[EXTERNAL BROADCAST] Triggering event for: ${action}`);

      // Create the event 
      const ocppEvent = new CustomEvent('ocpp-message-incoming', {
        detail: { action, payload, messageId }
      });

      // Dispatch to the whole window
      window.dispatchEvent(ocppEvent);
    }

    // --- 2. LOGGING LOGIC ---
    setMessages(prev => {
      // Check if this is a response to an outgoing message (Type 3)
      if (message.messageId) {
        const existingIndex = prev.findIndex(m => m.messageId === message.messageId && m.direction === 'outgoing');
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            response: message.payload || message,
            responseTimestamp: new Date(),
            hasResponse: true
          };
          return updated;
        }
      }

      // Add as new incoming message
      return [{
        id: message.messageId || uuidv4(),
        messageId: message.messageId,
        timestamp: new Date(),
        direction: 'incoming',
        action: message.action,
        payload: message.payload || message
      }, ...prev.slice(0, 99)];
    });
  }, []);

  const handleConnectionChange = useCallback((connected: boolean) => {
    setIsConnected(connected);
    localStorage.setItem('websocket-connected', connected.toString());

    if (connected) {
      console.log(`Connected to CCMS as station ${stationId}`);
    } else {
      console.log(`Disconnected from CCMS`);
    }
  }, [stationId]);

  const handleError = useCallback((error: string) => {
    console.error('OCPP Client Error:', error);
  }, []);

  useEffect(() => {
    if (ocppClient && ocppClient.getStationId() === stationId) return;

    if (ocppClient) ocppClient.disconnect();

    const client = new OCPPClient(
      stationId,
      handleIncomingMessage,
      handleConnectionChange,
      handleError
    );
    setOcppClient(client);

    const wasConnected = localStorage.getItem('websocket-connected') === 'true';
    if (wasConnected) {
      setTimeout(() => client.connect(), 100);
    }
  }, [stationId, handleIncomingMessage, handleConnectionChange, handleError]); // Added full dependencies

  const connect = () => ocppClient?.connect();

  const disconnect = () => {
    ocppClient?.disconnect();
    localStorage.removeItem('websocket-connected');
  };

  const sendHeartbeat = () => {
    if (ocppClient) {
      const messageId = ocppClient.sendHeartbeat();
      addOutgoingMessage('Heartbeat', {}, messageId);
    }
  };

  const sendBootNotification = () => {
    if (ocppClient) {
      const messageId = ocppClient.sendBootNotification();
      addOutgoingMessage('BootNotification', {
        reason: 'PowerUp',
        chargingStation: {
          vendorName: 'SimulatorCorp',
          model: 'SimCharger-Pro',
          serialNumber: `SC-${stationId}`,
          firmwareVersion: '1.0.0'
        }
      }, messageId);
    }
  };

  const addOutgoingMessage = useCallback((action: string, payload: any, messageId: string) => {
    setMessages(prev => [{
      id: messageId,
      timestamp: new Date(),
      direction: 'outgoing',
      action,
      payload,
      messageId
    }, ...prev.slice(0, 99)]);
  }, []);

  return (
    <WebSocketContext.Provider value={{
      isConnected,
      stationId,
      setStationId,
      messages,
      connect,
      disconnect,
      sendHeartbeat,
      sendBootNotification,
      ocppClient,
      addOutgoingMessage
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};